import { fetchAllWidgetsHtml } from '@/components/DesignSystem/EmptyWidget/emptyWidgetApi';
import { StoreDetails } from '@/redux/reducers';
import { NextPageContext } from 'next';
import {
  fetchStoreInfo,
  getThemeById,
  getDummyProductsByThemeTagId,
  getPublishedBlogs,
} from '../services';
// import { generateTemplateWidgetFile } from './generateTemplateWidgetFile';
import { fetchSubscriptionInfo, getCouponList, getFeatureLocks } from '../services/store';
import { clearSSRStore, getSSRStore, updateSSRStore } from '../redux';
import { fetchDomainFromHeaders } from '@/utils/fetchDomainFromHeaders';
import { isWebsiteInternallyRouted } from 'src/utils/isWebsiteInternallyRouted';
import { setDSDomainFromCtx } from './getShowroomUrlForStore';
import { ssrDataCall } from './ssrCommonApiCall';
// import {
// loadReactComponent,
// loadReactElement,
// loadWidgetStyles,
// } from '@/components/WidgetMaker/widgetLoader/moduleLoader';
// import { scopeStyles } from '@/components/WidgetMaker/WidgetDnD/RenderWidget';
// import { getRenderedComponent } from './ssrPageDataUtils';
import { getAdditionalPagesData } from '@/components/WidgetMaker/widgetMakerUtils';
import { getWidgetSSRData } from '@/utils/getWidgetSSRData';
import { redirectStoreIfNeeded, redirectTo404, isRouteHomepage } from '@/utils/routes';
import merge from 'lodash/merge';
import { fetchAdditionalPageSeoDetails } from './fetchAdditionalPageSeoDetails';
import { SUBSCRIPTION_FLAG } from './constants';
import { checkCustomThemeShopUrl } from './checkCustomThemeShopUrl';
import {
  NEW_STORE_WEBSITE_STATUS,
  NEW_SUBSCRIPTIONS_STATUS,
} from 'src/constants/subscription';
import { getBasicWebsiteInfoByCTX } from 'src/pageUtils/commonUtils/ctxUtils';

const ADDITIONAL_DATA = {
  theme: 'theme',
  collections: 'collections',
  categories: 'categories',
  themeHtml: 'themeHtml',
  seo: 'seo',
  coupons: 'coupons',
  additionalPagesList: 'additionalPagesList',
  advancedSeo: 'advancedSeo',
  canonicalData: 'canonicalData'
} as const;

type AdditionalDataToFetch = (keyof typeof ADDITIONAL_DATA)[];

interface SSRCollectiveData {
  collections: any;
  catalogCategories: any;
  catalogTags: any;
  seo: any;
  canonicalData: any;
}

const fetchThemeFromCtx = async (ctx: NextPageContext, storeDetails: StoreDetails) => {
  const { tid = null } = ctx.query;

  if (typeof ctx.req?.headers?.host == 'string' && tid) {
    const themeResponse = await getThemeById(tid, storeDetails?.store_id);
    if (themeResponse.data && themeResponse.data.status) {
      return themeResponse.data.theme || null;
    }
  }
};

const fetchDummyCatalogItemsAndCategoryFromCtx = async (
  ctx: NextPageContext,
  storeDetails: StoreDetails
) => {
  // extract out theme id, tag id, and available items flag from query params
  let { tid = null, tag_id = null, items_available = 1 } = ctx.query;
  items_available = +items_available;
  const shouldFetchDummyData = !storeDetails?.premium || !items_available;
  if (
    typeof ctx.req?.headers?.host == 'string' &&
    tid &&
    tag_id &&
    shouldFetchDummyData
  ) {
    const dummyCatalogResponse = await getDummyProductsByThemeTagId(tag_id);
    if (dummyCatalogResponse?.data?.status) {
      const catalogCategoryItems = dummyCatalogResponse?.data?.store_items;

      // transform data to make it suitable for mb6
      const dummyCatalog = catalogCategoryItems?.reduce(
        (acc, data, index) => {
          acc.categories.push(data.category);
          acc.catalog_items[data.category.id] = data.items;
          return acc;
        },
        { categories: [], catalog_items: {} }
      );
      return dummyCatalog;
    } else {
      return null;
    }
  }
};

const getSSRTypesToFetch = (additionalDataToFetch: AdditionalDataToFetch) => {
  const ssrTypesToFetch = [];
  if (additionalDataToFetch.includes(ADDITIONAL_DATA.categories)) {
    ssrTypesToFetch.push(ADDITIONAL_DATA.categories);
  }
  if (additionalDataToFetch.includes(ADDITIONAL_DATA.collections)) {
    ssrTypesToFetch.push(ADDITIONAL_DATA.collections);
  }
  if (additionalDataToFetch.includes(ADDITIONAL_DATA.seo)) {
    ssrTypesToFetch.push(ADDITIONAL_DATA.seo);
  } 
  if (additionalDataToFetch.includes(ADDITIONAL_DATA.canonicalData)) {
    ssrTypesToFetch.push(ADDITIONAL_DATA.canonicalData);
  } 
  return ssrTypesToFetch;
};

interface I_Extra_Configs {
  additionalDataToFetch?: AdditionalDataToFetch;
  advancedSeoReqConfigs?: {
    typeId: any;
    type: string;
  };
  checkForSubscriptionExpiry?: boolean;
  checkForCustomThemeShopUrl?: boolean;
  canonicalParams?: {
    type?: number;
    typeId: number;
    slug_url: String;
  };
}

export const getSSRStoreData = async (
  ctx: NextPageContext,
  {
    additionalDataToFetch = [],
    advancedSeoReqConfigs = null,
    checkForSubscriptionExpiry = false,
    checkForCustomThemeShopUrl = false,
    canonicalParams = null,
  }: I_Extra_Configs = {}
) => {
  setDSDomainFromCtx(ctx);
  const { domain, isCustomDomain } = fetchDomainFromHeaders(ctx);
  clearSSRStore(domain);
  const { isBuilderRoute, isPDPRoute } = getBasicWebsiteInfoByCTX(ctx);
  let subscriptionData = {}

  const storeResponse = await fetchStoreInfo({
    forBuilder: isBuilderRoute,
    ctx,
  });
  const storeInfo = storeResponse?.data?.store as StoreDetails;
  if (isBuilderRoute) {
    subscriptionData = await fetchSubscriptionInfo({
      ctx,
    });
    if (storeInfo && subscriptionData) {
      storeInfo.subscriptionData = subscriptionData || {};
    }
  }
  let ssrCollectiveData: SSRCollectiveData;
  let dummyCatalog = null;

  updateSSRStore({ storeReducer: { store: storeInfo || null } }, domain);

  const isDomainInactive =
    // Premium flag is kept unset for new subscriptions
    storeInfo?.premium === 0 &&
    storeInfo?.theme?.new_subscription_web_status ===
    NEW_STORE_WEBSITE_STATUS.ACTIVE_NOT_LIVE;

  if (
    isDomainInactive &&
    !isWebsiteInternallyRouted(ctx.req.headers?.domain || ctx.req.headers.host)
  ) {
    if (isRouteHomepage(ctx)) {
      redirectTo404(ctx);
      return;
    }
  }

  if (redirectStoreIfNeeded(ctx, storeResponse?.data)) {
    return;
  }

  // In case, merchant's subscription has expired, redirect to home page
  // const pathName = ctx.req.url?.split(domain)?.[1] || '/';
  if (
    checkForSubscriptionExpiry &&
    storeInfo.subscription_flag === SUBSCRIPTION_FLAG.EXPIRED
    // && pathName !== PATH.DOMAIN_HOME
  ) {
    ctx?.res?.writeHead(302, { location: '/' });
    return;
  }

  // For Custom Websites "/shop" will throw Page Not Found
  if (checkForCustomThemeShopUrl) {
    const isCustomThemeStore = storeInfo?.theme?.custom_theme_flag;
    if (checkCustomThemeShopUrl(ctx.req.url, isCustomThemeStore)) {
      redirectTo404(ctx);
      return;
    }
  }

  let advCustomSeoDetails = {};

  if (storeInfo?.status && !storeInfo?.redirect_url) {
    const additionalDataPromises: Promise<unknown>[] = [];

    // Widget handlers
    const widgetDataMap = await getWidgetSSRData({ storeInfo, ctx, domain });

    updateSSRStore(
      {
        jsonWidgetsReducer: {
          ...widgetDataMap.data,
          isWidgetLoaded: true,
          // widgetsTemplateConfigs,
        },
      },
      domain
    );

    /**
     * Required to fetch themes by id during previews, which are not applied permanently,
     * & these are given precendece over store's theme data during the previews.
     */
    if (additionalDataToFetch.includes(ADDITIONAL_DATA.theme)) {
      const theme = await fetchThemeFromCtx(ctx, storeInfo);
      storeInfo.theme = theme || storeInfo.theme;
      dummyCatalog = await fetchDummyCatalogItemsAndCategoryFromCtx(ctx, storeInfo);
    }

    // Static widgets HTML
    if (additionalDataToFetch.includes(ADDITIONAL_DATA.themeHtml)) {
      const allWidgetsHtmlPromise = fetchAllWidgetsHtml(storeInfo.theme).then(
        (allWidgetsHtml) => {
          const bodyComponents = storeInfo?.theme?.components.Body?.map((comp) => {
            if (allWidgetsHtml[comp.widget_id]) {
              comp.html = allWidgetsHtml[comp.widget_id];
            }
            return comp;
          });

          updateSSRStore(
            {
              storeReducer: {
                store: { theme: { components: { Body: bodyComponents } } },
              },
            },
            domain
          );
        }
      );
      additionalDataPromises.push(allWidgetsHtmlPromise);
    }

    // This is a temporary workaround fix to check whether the 404 reroute issue is caused by
    // featureLocks api call in this method or not
    if (!isPDPRoute) {
      const { data: featureLocks } = await getFeatureLocks(storeInfo?.store_id);

      if (featureLocks) {
        updateSSRStore(
          {
            commonReducer: {
              featureLocksData: {
                isFeatureLocksLoaded: true,
                featureLocks,
              },
            },
          },
          domain
        );
      }

      const isSeoFeatureUnlocked =
        additionalDataToFetch?.includes('advancedSeo') &&
        !featureLocks?.['seo']?.is_locked;
      if (!!isSeoFeatureUnlocked) {
        advCustomSeoDetails = await fetchAdditionalPageSeoDetails(
          merge({ storeInfo: storeResponse?.data?.store }, advancedSeoReqConfigs)
        );
      }

      // Collective SSR Data
      const ssrTypeToFetch = getSSRTypesToFetch(additionalDataToFetch);
      const ssrCollectiveDataPromise = ssrDataCall(
        ssrTypeToFetch,
        storeInfo,
        featureLocks,
        canonicalParams,
      ).then((data: any) => {
        ssrCollectiveData = data;
      });
      additionalDataPromises.push(ssrCollectiveDataPromise);
    }

    if (additionalDataToFetch.includes(ADDITIONAL_DATA.coupons)) {
      const couponsListPromise = getCouponList(storeInfo?.store_id).then((couponList) => {
        updateSSRStore(
          {
            promoReducer: {
              couponsList: couponList?.coupons?.slice(0, 3) || [],
            },
          },
          domain
        );
      });
      additionalDataPromises.push(couponsListPromise);
    }

    if (
      ((storeInfo.theme?.custom_theme_flag && storeInfo.theme?.additional_page_flag) ||
        storeInfo.new_subscription_status !== NEW_SUBSCRIPTIONS_STATUS.INACTIVE) &&
      additionalDataToFetch.includes(ADDITIONAL_DATA.additionalPagesList)
    ) {
      const navigationListPromise = getAdditionalPagesData(storeInfo?.store_id);
      const blogListPromise = getPublishedBlogs(storeInfo?.store_id);

      const combinedPromise = Promise.all([navigationListPromise, blogListPromise]).then(
        ([{ navigationPageList, additionalPagesList }, blogListResponse]) => {
          updateSSRStore(
            {
              additionalPagesReducer: {
                navigationList: navigationPageList,
                additionalPageList: additionalPagesList,
                ...(!!blogListResponse?.length && { newBlogList: blogListResponse }),
              },
            },
            domain
          );
        }
      );

      additionalDataPromises.push(combinedPromise);
    }

    // Wait for all data promises to resolve
    await Promise.all(additionalDataPromises);

    const {
      collections = [],
      catalogCategories = [],
      catalogTags = [],
      canonicalData = {}
    } = ssrCollectiveData || {};

    updateSSRStore(
      {
        catalogReducer: {
          collections: collections,
          categories: dummyCatalog?.categories || catalogCategories,
          productTags: catalogTags,
          catalog_items: dummyCatalog?.catalog_items || null,
          canonicalData: canonicalData,
        },
      },
      domain
    );
  }

  setDSDomainFromCtx(ctx);

  const ssrStore = getSSRStore(domain);

  const propData = {
    isCustomDomain,
    domain,
    ssrStore,
    seo: ssrCollectiveData?.seo || null,
    advCustomSeoDetails,
  };

  clearSSRStore(domain);

  return propData;
};
